import { Outlet } from '@tanstack/react-router'
import { Navbar } from '@/common/components/navbar'
import { Hotjar } from '@/common/components/hotjar'
import { REDACT_ATTRS } from '@/common/analytics'
import { useProductTour, TourProvider } from '@/features/product-tour'
import { AgentTrainingTourProvider } from '@/features/agent-training-tour'
import * as styles from '@/common/components/authenticated-layout/AuthenticatedLayout.css'

// Auth checks, onboarding redirect, and cross-tab sync are handled by AuthSyncAndRedirect in __root.tsx
export const Route = createFileRoute({
  component: AuthLayout,
})

// Exported so the redaction baseline can be unit-tested in isolation
// (`_auth.test.tsx`) without the codegen-injected `Route`/router context.
export function AuthLayoutInner() {
  // Product tour auto-launches for investors who haven't completed it yet
  useProductTour()

  // This is the MAIN authenticated shell — it wraps the entire routed
  // workspace (offers, properties, buybox, admin, settings, support).
  //
  // Global DOM redaction baseline (defense-in-depth).
  // Heap's autocapture is already globally off via `disableTextCapture: true`
  // at load; this is the in-DOM backstop if that config ever regresses.
  // `data-heap-redact-text` masks all in-shell text content from autocapture,
  // and `data-heap-redact-attributes` masks the listed attribute values (which
  // carry PII even when text capture is off). Both cascade to the whole subtree
  // below this root. Heap still records that an event happened and on what
  // route — pageviews, sessions, and clicks are unaffected; only the sensitive
  // CONTENT is replaced with `****`.
  //
  // NOTE: this does NOT cover portal-rendered subtrees (Radix dialogs/drawers
  // mount outside this container) — those carry their own field-level <Redact>.
  return (
    <div
      className={styles.layout}
      data-heap-redact-text="true"
      data-heap-redact-attributes={REDACT_ATTRS}
    >
      <Hotjar />
      <Navbar />
      <main className={styles.content}>
        <Outlet />
      </main>
    </div>
  )
}

function AuthLayout() {
  // AgentTrainingTourProvider wraps the app so its Welcome and Completion
  // modals can render anywhere, and PropertyForm / create.tsx can call
  // `useAgentTrainingTourContext().requestCompletion(...)` from any route.
  return (
    <TourProvider>
      <AgentTrainingTourProvider>
        <AuthLayoutInner />
      </AgentTrainingTourProvider>
    </TourProvider>
  )
}
